/**
 * LearnerProgressDetailModal Component (Admin)
 *
 * 학습자 학습 상세 현황 모달
 * - 전체 진도율/완료 레슨/정답률 요약
 * - Course → Module → Lesson → Component 트리 뷰
 */

import { useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Loader2, AlertTriangle, ChevronDown, ChevronRight,
  Play, FileText, PenTool, CheckCircle2, Clock, MinusCircle, X
} from 'lucide-react'
import {
  learningReportApi,
  type LearnerProgressDetailModalData,
  type CourseProgressDetail,
  type ModuleProgressDetail,
  type LessonProgressDetail,
  type ComponentProgressDetail,
} from '@/api/learningReport'

// ============================================================================
// Types
// ============================================================================

export interface LearnerProgressDetailModalProps {
  userId: number
  classProductId: number
  learnerName: string
  className: string
  isOpen: boolean
  onClose: () => void
}

// ============================================================================
// Helper Functions
// ============================================================================

const locationLabels: Record<string, string> = {
  video: '영상',
  pre_quiz: '사전 퀴즈',
  post_quiz: '사후 퀴즈',
  wrapup_quiz: '마무리 퀴즈',
  checkup_quiz: '체크업 퀴즈',
  assignment: '과제',
  module_start_quiz: '모듈 시작 퀴즈',
  module_end_quiz: '모듈 마무리 퀴즈',
}

const statusConfig: Record<string, { label: string; className: string }> = {
  completed: { label: '완료', className: 'bg-green-100 text-green-700' },
  in_progress: { label: '진행중', className: 'bg-blue-100 text-blue-700' },
  not_started: { label: '미시작', className: 'bg-gray-100 text-gray-500' },
}

function getStatusBadge(status: string) {
  const config = statusConfig[status] || statusConfig.not_started
  return (
    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${config.className}`}>
      {config.label}
    </span>
  )
}

function getStatusIcon(status: string) {
  switch (status) {
    case 'completed':
      return <CheckCircle2 className="w-4 h-4 text-green-500" />
    case 'in_progress':
      return <Clock className="w-4 h-4 text-blue-500" />
    default:
      return <MinusCircle className="w-4 h-4 text-gray-400" />
  }
}

function getComponentIcon(type: string) {
  switch (type) {
    case 'video':
      return <Play className="w-3.5 h-3.5 text-purple-500" />
    case 'quiz':
      return <FileText className="w-3.5 h-3.5 text-amber-500" />
    case 'assignment':
      return <PenTool className="w-3.5 h-3.5 text-teal-500" />
    default:
      return <FileText className="w-3.5 h-3.5 text-gray-400" />
  }
}

function formatWatchTime(seconds: number | null): string {
  if (seconds == null || seconds === 0) return '-'
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  if (mins === 0) return `${secs}초`
  return `${mins}분 ${secs}초`
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-'
  try {
    return new Date(dateStr).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  } catch {
    return '-'
  }
}

// ============================================================================
// Component
// ============================================================================

export function LearnerProgressDetailModal({
  userId,
  classProductId,
  learnerName,
  className,
  isOpen,
  onClose,
}: LearnerProgressDetailModalProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['adminLearnerProgressDetailModal', userId, classProductId],
    queryFn: async () => {
      const res = await learningReportApi.getLearnerProgressDetail(userId, classProductId)
      return res.data?.data ?? null
    },
    enabled: isOpen && userId > 0 && classProductId > 0,
    staleTime: 60 * 1000,
  })

  if (!isOpen) return null

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-slate-900">
            학습 상세 현황 - {learnerName} ({className})
          </h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
            </div>
          ) : error || !data ? (
            <div className="py-20 text-center">
              <AlertTriangle className="mx-auto mb-4 w-12 h-12 text-amber-500" />
              <p className="text-gray-500">학습 상세 현황을 불러올 수 없습니다.</p>
            </div>
          ) : (
            <>
              <SummarySection summary={data.summary} progressPercent={data.progressPercent} />
              <div className="mt-6">
                <h3 className="mb-3 text-lg font-semibold text-gray-900">코스별 진행 현황</h3>
                {data.courses.length === 0 ? (
                  <p className="py-8 text-sm text-center text-gray-500">코스 데이터가 없습니다.</p>
                ) : (
                  <div className="space-y-3">
                    {data.courses.map((course) => (
                      <CourseAccordion key={course.contentId} course={course} />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-6 py-2.5 w-full font-semibold text-white rounded-xl transition-colors bg-slate-800 hover:bg-slate-900"
          >
            닫기
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ============================================================================
// Sub Components
// ============================================================================

function SummarySection({ summary, progressPercent }: {
  summary: LearnerProgressDetailModalData['summary']
  progressPercent: number
}) {
  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
      <div className="p-4 bg-gray-50 rounded-xl">
        <p className="mb-1 text-xs text-gray-500">전체 진도율</p>
        <p className="text-2xl font-bold text-amber-600">{progressPercent.toFixed(0)}%</p>
        <div className="overflow-hidden mt-2 h-2 bg-gray-200 rounded-full">
          <div
            className="h-full rounded-full transition-all duration-500 bg-amber-500"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>
      <div className="p-4 bg-gray-50 rounded-xl">
        <p className="mb-1 text-xs text-gray-500">완료 레슨</p>
        <p className="text-2xl font-bold text-gray-900">
          {summary.completedLessons}
          <span className="text-sm font-normal text-gray-500">/{summary.totalLessons}</span>
        </p>
      </div>
      <div className="p-4 bg-gray-50 rounded-xl">
        <p className="mb-1 text-xs text-gray-500">레슨 정답률</p>
        <p className="text-2xl font-bold text-gray-900">
          {summary.lessonQuizAccuracy != null ? `${summary.lessonQuizAccuracy.toFixed(1)}%` : '-'}
        </p>
      </div>
      <div className="p-4 bg-gray-50 rounded-xl">
        <p className="mb-1 text-xs text-gray-500">마지막 학습일</p>
        <p className="text-lg font-bold text-gray-900">{formatDate(summary.lastAccessedAt)}</p>
      </div>
    </div>
  )
}

function CourseAccordion({ course }: { course: CourseProgressDetail }) {
  const [isOpen, setIsOpen] = useState(true)

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex justify-between items-center px-4 py-3 w-full bg-white transition-colors hover:bg-gray-50"
      >
        <div className="flex gap-3 items-center">
          {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
          {getStatusIcon(course.status)}
          <span className="font-semibold text-gray-900">{course.title}</span>
          {getStatusBadge(course.status)}
        </div>
        <div className="flex gap-2 items-center">
          <div className="overflow-hidden w-20 h-2 bg-gray-200 rounded-full">
            <div className="h-full rounded-full bg-amber-500" style={{ width: `${course.progressPercent}%` }} />
          </div>
          <span className="w-10 text-xs text-right text-gray-500">{course.progressPercent.toFixed(0)}%</span>
        </div>
      </button>
      {isOpen && (
        <div className="border-t border-gray-100 bg-gray-50/50">
          {course.modules.length === 0 ? (
            <p className="py-4 text-sm text-center text-gray-500">모듈 없음</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {course.modules.map((mod) => (
                <ModuleAccordion key={mod.contentId} module={mod} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function ModuleAccordion({ module }: { module: ModuleProgressDetail }) {
  const [isOpen, setIsOpen] = useState(module.status === 'in_progress')

  return (
    <div>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-6 py-2.5 hover:bg-gray-100/50 transition-colors"
      >
        <div className="flex items-center gap-2.5">
          {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-gray-400" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />}
          {getStatusIcon(module.status)}
          <span className="text-sm font-medium text-gray-800">{module.title}</span>
          {getStatusBadge(module.status)}
          <span className="text-xs text-gray-400">
            ({module.completedChildren}/{module.totalChildren} 완료)
          </span>
        </div>
        <div className="flex gap-2 items-center">
          <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full rounded-full bg-amber-400" style={{ width: `${module.progressPercent}%` }} />
          </div>
          <span className="w-10 text-xs text-right text-gray-500">{module.progressPercent.toFixed(0)}%</span>
        </div>
      </button>
      {isOpen && (
        <div className="pr-4 pb-2 pl-12">
          {module.lessons.length === 0 ? (
            <p className="py-2 text-xs text-gray-400">레슨 없음</p>
          ) : (
            <div className="space-y-1.5">
              {module.lessons.map((lesson) => (
                <LessonRow key={lesson.contentId} lesson={lesson} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function LessonRow({ lesson }: { lesson: LessonProgressDetail }) {
  return (
    <div className="p-3 bg-white rounded-lg border border-gray-100">
      <div className="flex gap-2 items-center mb-2">
        {getStatusIcon(lesson.status)}
        <span className="text-sm text-gray-800">{lesson.title}</span>
        {getStatusBadge(lesson.status)}
      </div>
      {lesson.components.length > 0 && (
        <div className="ml-6 space-y-1">
          {lesson.components.map((comp, idx) => (
            <ComponentRow key={`${comp.type}-${comp.location}-${idx}`} component={comp} />
          ))}
        </div>
      )}
    </div>
  )
}

function ComponentRow({ component }: { component: ComponentProgressDetail }) {
  const locationLabel = locationLabels[component.location] || component.location
  const statusCfg = statusConfig[component.status] || statusConfig.not_started

  return (
    <div className="flex justify-between items-center px-2 py-1 text-xs bg-gray-50 rounded">
      <div className="flex gap-2 items-center">
        {getComponentIcon(component.type)}
        <span className="text-gray-600">{locationLabel}</span>
        <span className={`px-1.5 py-0.5 rounded-full font-medium ${statusCfg.className}`}>
          {statusCfg.label}
        </span>
      </div>
      <div className="flex gap-3 items-center text-gray-500">
        {component.quizScore != null && (
          <span>
            점수: <span className="font-medium text-gray-700">{component.quizScore}점</span>
            {component.quizPassed != null && (
              <span className={`ml-1 ${component.quizPassed ? 'text-green-600' : 'text-red-500'}`}>
                ({component.quizPassed ? '통과' : '미통과'})
              </span>
            )}
          </span>
        )}
        {component.videoWatchSeconds != null && component.videoWatchSeconds > 0 && (
          <span>시청: <span className="font-medium text-gray-700">{formatWatchTime(component.videoWatchSeconds)}</span></span>
        )}
      </div>
    </div>
  )
}
